<template>
    <kiosk-group-details :teamId="props.teamId" :groupId="props.groupId">
        <div class="flex flex-col w-full justify-center">
            <div class="max-w-(--breakpoint-sm) flex flex-col">
                <page-callout section="kiosk-group-billing-details" class="mb-4" />

                <page-header>Billing Details</page-header>

                <page-loading v-if="!billing"></page-loading>
                <template v-else>
                    <group-billing-email
                        class="mb-4"
                        :teamId="group.team_id"
                        :groupId="group.group_id"
                        :billingEmail="billing.billing_email"
                        :isEditable="isEditable"
                        :allowUnset="true"
                        :unsetting="unsettingEmail"
                        @unset="confirmUnsetEmail"
                    />

                    <group-billing-info
                        class="mb-4"
                        :teamId="group.team_id"
                        :groupId="group.group_id"
                        :billingInformation="{
                            billing_company_legal_name: billing.billing_company_legal_name,
                            billing_address: billing.billing_address,
                            billing_address_line_2: billing.billing_address_line_2,
                            billing_city: billing.billing_city,
                            billing_state: billing.billing_state,
                            billing_zip: billing.billing_zip,
                            billing_country: billing.billing_country,
                            billing_po_number: billing.billing_po_number,
                        }"
                        :isEditable="isEditable"
                        :allowUnset="true"
                        :unsetting="unsettingInformation"
                        @unset="confirmUnsetInformation"
                    />

                    <group-stripe-customer v-if="stripeKey && isEditable" class="mb-4" :stripeId="billing.stripe_id" />
                </template>
            </div>
        </div>
    </kiosk-group-details>
</template>

<script setup>
import { VxSwal } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import KioskGroupDetails from '~/components/kiosk/kiosk-groups/details/KioskGroupDetails.vue';
import { useGroupDetails } from '~/components/kiosk/kiosk-groups/composables/useGroupDetails';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import GroupBillingEmail from '~/components/settings/billing/GroupBillingEmail.vue';
import GroupBillingInfo from '~/components/settings/billing/GroupBillingInfo.vue';
import GroupStripeCustomer from '~/components/settings/billing/GroupStripeCustomer.vue';
import { useToasted } from '~/composables/useToasted';
import { adminGroupsClient } from '~/services';
import { throwSwalRequestError } from '~/utils/request';

const { group, billing, getBilling, onMountedGroup } = useGroupDetails();

const props = defineProps({
    teamId: { type: Number, required: true },
    groupId: { type: String, required: true },
});

const stripeKey = Spark.stripeKey;
const isEditable = computed(() => group.value.is_billable);

const toasted = useToasted();
const unsettingInformation = ref(false);
const unsettingEmail = ref(false);

const confirmUnsetInformation = () => {
    VxSwal.confirmation(
        'Unset billing information',
        'This permanently clears the billing address, company name, and PO number for this group. This action cannot be undone.',
        'danger',
        () => unsetInformation()
    );
};

const unsetInformation = async () => {
    unsettingInformation.value = true;
    try {
        await adminGroupsClient.unsetBillingInformation(props.teamId, props.groupId);
        await getBilling();
        toasted.success('Billing information cleared.');
    } catch (e) {
        throwSwalRequestError(e);
    } finally {
        unsettingInformation.value = false;
    }
};

const confirmUnsetEmail = () => {
    VxSwal.confirmation(
        'Unset billing email',
        'This permanently clears the billing email for this group. This action cannot be undone.',
        'danger',
        () => unsetEmail()
    );
};

const unsetEmail = async () => {
    unsettingEmail.value = true;
    try {
        await adminGroupsClient.unsetBillingEmail(props.teamId, props.groupId);
        await getBilling();
        toasted.success('Billing email cleared.');
    } catch (e) {
        throwSwalRequestError(e);
    } finally {
        unsettingEmail.value = false;
    }
};

onMountedGroup(() => {
    getBilling();
});
</script>
